import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authlog } from 'AuthLogger';
import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import { pageDataService } from 'common/utilities';
import { isCMApiProtectionEnabled } from 'common/utilities/FlightSetting';
import { localStorageService } from 'common/utilities/LocalStorageService';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import { IdleDetectionContext } from 'features/creditMonitoring/IdleDetectionProvider';
import { AuthResult } from 'nativeAppShim';
import { SessionState, setSessionState, useAppDispatch, useAppSelector } from 'store';
import { useWebView } from 'store/webView/useWebView';
import { useMsalAuth } from 'useMsalAuth';

import { getCmAuthCookieExpiration, isCmAuthCookieValid, removeCmAuthCookieExpiry } from './CreditAccessHelpers';
import { CM_SESSION_IDLE_TIMEOUT } from './CreditMonitoringConstants';

const STALE_TIMEOUT = 1000 * 60 * 8; // 8 minutes

const STALE_TO_LOCKED_DURATION = CM_SESSION_IDLE_TIMEOUT - STALE_TIMEOUT; // 2 minutes

export interface IActiveSessionState {
  sessionState: SessionState;
  timeUntilLocked: number;
  unlockSession: () => void;
  extendSession: () => void;
  isMfaAuthenticated: boolean;
  getMfaTokenSilent: () => Promise<string | undefined>;
  loginMfa: () => Promise<void>;
  getAuthCookie: () => Promise<void>;
  hasAuthCookie: boolean;
  isCreditDialogOpen: boolean;
  setIsCreditDialogOpen: (isOpen: boolean) => void;
}

const defaultActiveSessionState: IActiveSessionState = {
  sessionState: SessionState.Unknown,
  timeUntilLocked: -1,
  unlockSession: () => {},
  extendSession: () => {},
  isMfaAuthenticated: false,
  getMfaTokenSilent: async () => undefined,
  loginMfa: async () => {},
  getAuthCookie: async () => {},
  hasAuthCookie: false,
  isCreditDialogOpen: false,
  setIsCreditDialogOpen: () => {},
};

export const ActiveSessionContext = createContext<IActiveSessionState>(defaultActiveSessionState);

interface IActiveSessionProviderProps {
  children: JSX.Element;
}

/**
 * Provider only manages the session state and timers.
 * @param root0
 * @param root0.children
 */
export const ActiveSessionProviderInternal = ({ children }: IActiveSessionProviderProps) => {
  const { reactivate: updateLastActiveTime, lastActiveTime } = useContext(IdleDetectionContext);
  const { isMfaAuthenticated, getMfaTokenSilent, loginMfa } = useMsalAuth(pageDataService.getUserData().userPrincipalName);
  const { isWebView, supportsLocalAuth, authResult } = useWebView();
  const componentKey = useAppSelector((state) => state.webView.componentKey);
  const sessionState = useAppSelector((state) => state.auth.sessionState);
  const dispatch = useAppDispatch();

  const telemetryPrefix = 'ActiveSessionProvider';

  const getCurrentSessionState = useCallback((): SessionState => {
    if (localStorageService.isSessionActive() || isCmAuthCookieValid()) {
      const message = 'ActiveSessionProvider => initial session state is active';
      authlog(`${message} (localStorage: ${localStorageService.isSessionActive()}, cmAuthCookie: ${isCmAuthCookieValid()})`);
      return SessionState.Active;
    }
    authlog(`ActiveSessionProvider => initial session state is locked`);
    return SessionState.Locked;
  }, []);

  useEffect(
    () => {
      // Sets the initial session state after mounting a component.
      dispatch(setSessionState(getCurrentSessionState()));
    },
    // componentKey is used to trigger this effect on client-side navigation for a cached webview.
    [componentKey, dispatch, getCurrentSessionState]
  );

  const hasAuthCookie = isCmAuthCookieValid();
  const [timeUntilLocked, setTimeUntilLocked] = useState(sessionState === SessionState.Active ? CM_SESSION_IDLE_TIMEOUT : 0);

  const [isCreditDialogOpen, setIsCreditDialogOpen] = useState(false);

  // Session state transition timers
  const staleTimeoutId = useRef<NodeJS.Timeout | null>(null); // ID for time until STALE state
  const lockTimeoutId = useRef<NodeJS.Timeout | null>(null); // ID for time until LOCKED state

  // Countdown timer
  const timeUntilLockedIntervalId = useRef<NodeJS.Timer | null>(null);

  // During stale (countdown timer) state, check if the session was renewed in another window
  const staleStateCookieCheckIntervalId = useRef<NodeJS.Timer | null>(null);

  const clearTimers = () => {
    if (timeUntilLockedIntervalId.current !== null) {
      clearInterval(timeUntilLockedIntervalId.current);
    }
    if (staleStateCookieCheckIntervalId.current !== null) {
      clearInterval(staleStateCookieCheckIntervalId.current);
    }
    if (staleTimeoutId.current !== null) {
      clearTimeout(staleTimeoutId.current);
    }
    if (lockTimeoutId.current !== null) {
      clearTimeout(lockTimeoutId.current);
    }
  };

  /**
   * Clear timers to prevent state updates on the unmounted component.
   */
  useEffect(() => clearTimers, []);

  /**
   * Sets the session to LOCKED state and clears all intervals.
   */
  const setLockedState = useCallback(() => {
    authlog(`ActiveSessionProvider => ⏳ will now enter LOCKED state`);
    TelemetryProvider.Instance.trackEvent(`${telemetryPrefix}_EnterLockedState`);
    dispatch(setSessionState(SessionState.Locked));
    setTimeUntilLocked(0);
    if (timeUntilLockedIntervalId.current !== null) {
      clearInterval(timeUntilLockedIntervalId.current);
    }
    if (staleStateCookieCheckIntervalId.current !== null) {
      clearInterval(staleStateCookieCheckIntervalId.current);
    }
  }, []);

  const getAuthCookie = useCallback(async () => {
    const handleError = (message: string, telemetryProps: { errorType: string; message?: string }) => {
      authlog(message);
      TelemetryProvider.Instance.trackEvent(`${telemetryPrefix}_GetAuthCookieError`, telemetryProps);
      setLockedState();
    };

    if (!isCMApiProtectionEnabled()) {
      authlog('🚦 CM API protection is disabled');
      return;
    }

    if (hasAuthCookie) {
      authlog('getAuthCookie => refreshing auth cookie');
      try {
        await microsoftConsumerSecurityClient.credit.refreshAuthCookie();
      } catch (e) {
        handleError(`getAuthCookie => Error refreshing auth cookie: ${e}`, { errorType: 'RefreshAuthCookie', message: e as string });
      }
    } else if (isWebView) {
      try {
        authlog('getAuthCookie => getting new auth cookie for webview');
        await microsoftConsumerSecurityClient.credit.getWebViewAuthCookie({
          customHeaders: { 'SA-Auth': 'validated' },
        });
      } catch (e) {
        handleError(`getAuthCookie => Error getting auth cookie in webview: ${e}`, {
          errorType: 'GetWebViewAuthCookie',
          message: e as string,
        });
      }
    } else {
      const token = await getMfaTokenSilent();
      if (!token) {
        handleError('getAuthCookie => failed to get MFA token', { errorType: 'GetMfaToken' });
        return;
      }

      try {
        authlog('getAuthCookie => getting new auth cookie');
        await microsoftConsumerSecurityClient.credit.getAuthCookie({
          customHeaders: { Authorization: `Bearer ${token}` },
        });
      } catch (e) {
        handleError(`getAuthCookie => Error getting new auth cookie: ${e}`, { errorType: 'GetNewAuthCookie', message: e as string });
      }
    }
  }, [getMfaTokenSilent, hasAuthCookie, isWebView, setLockedState]);

  useEffect(() => {
    if (sessionState === SessionState.Active) {
      authlog('ActiveSessionProvider => session is active, fetching auth cookie');
      getAuthCookie();
    } else if (sessionState === SessionState.Locked) {
      authlog('ActiveSessionProvider => session is locked, removing auth cookie expiry');
      removeCmAuthCookieExpiry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionState]);

  // While user is in stale state, checks if the cookie was renewed and if the session should be unlocked again.
  const staleStateCookieCheck = useCallback(() => {
    const currentTime = Date.now();
    const cookieExpiryTime = getCmAuthCookieExpiration();
    const expectedStaleTime = cookieExpiryTime - STALE_TO_LOCKED_DURATION;

    // Session was renewed in another window. The session will be unlocked and set to ACTIVE.
    if (currentTime < expectedStaleTime) {
      authlog(`ActiveSessionProvider => existing stale state due to cookie renewal, setting sessionState = ACTIVE`);

      dispatch(setSessionState(SessionState.Active));
      clearTimers();

      renewStaleState(expectedStaleTime);
    }
  }, []);

  /**
   * Callback when stale state timer is completed.
   * Checks if the session was renewed (and set a new timer for the STALE state), or sets the session to STALE state.
   */
  const handleStaleState = useCallback(() => {
    authlog(`ActiveSessionProvider => handleStaleState called`);
    const currentTime = Date.now();
    const cookieExpiryTime = getCmAuthCookieExpiration();
    const expectedStaleTime = cookieExpiryTime - STALE_TO_LOCKED_DURATION;

    clearTimers();
    if (expectedStaleTime <= currentTime) {
      setStaleState();
    } else {
      authlog(`ActiveSessionProvider => stale state postponed due to cookie renewal`);
      renewStaleState(expectedStaleTime);
    }
  }, []);

  /**
   * Renews the stale state timer to the given expectedStaleTime.
   */
  const renewStaleState = useCallback((expectedStaleTime: number) => {
    const currentTime = Date.now();
    const timeRemainingUntilStale = Math.max(0, expectedStaleTime - currentTime);
    authlog(`ActiveSessionProvider => renewing stale state by ${timeRemainingUntilStale}ms`);

    staleTimeoutId.current = setTimeout(handleStaleState, timeRemainingUntilStale);
  }, []);

  /**
   * Sets the session to STALE state and starts the timer for LOCKED state.
   * Also starts the interval for updating the countdown and the interval for checking if the cookie was renewed in another window.
   */
  const setStaleState = useCallback(() => {
    authlog(`ActiveSessionProvider => ⚠️ will now enter STALE state`);
    TelemetryProvider.Instance.trackEvent(`${telemetryPrefix}_EnterStaleState`);
    dispatch(setSessionState(SessionState.Stale));
    setTimeUntilLocked(STALE_TO_LOCKED_DURATION);
    timeUntilLockedIntervalId.current = setInterval(() => {
      setTimeUntilLocked((prevTime) => {
        return Math.max(0, prevTime - 1000);
      });
    }, 1000);
    staleStateCookieCheckIntervalId.current = setInterval(staleStateCookieCheck, 2000);
    lockTimeoutId.current = setTimeout(setLockedState, STALE_TO_LOCKED_DURATION);
  }, []);

  /**
   * Mark session as active & start the timer for STALE state.
   */
  const activateSession = useCallback(
    (opts: { skipActiveTimeUpdate?: boolean } = {}) => {
      const { skipActiveTimeUpdate = false } = opts;

      dispatch(setSessionState(SessionState.Active));
      authlog(`ActiveSessionProvider => activateSession() => starting STALE and LOCKED timers`);
      localStorageService.reactivateSession();
      if (!skipActiveTimeUpdate) {
        updateLastActiveTime();
      }

      clearTimers();
      staleTimeoutId.current = setTimeout(handleStaleState, STALE_TIMEOUT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateLastActiveTime]
  );

  /**
   * Manually unlock a session that needs re-authentication.
   */
  const unlockSession = useCallback(() => {
    if (sessionState === SessionState.Locked) {
      authlog(`ActiveSessionProvider => sessionState is LOCKED. Calling activateSession()`);
      TelemetryProvider.Instance.trackEvent(`${telemetryPrefix}_SessionUnlocked`);
      activateSession();
    }
  }, [activateSession, sessionState]);

  /**
   * Extend the session via WarningCard CTA
   */
  const extendSession = useCallback(() => {
    if (sessionState === SessionState.Stale) {
      authlog(`ActiveSessionProvider => sessionState is STALE. Calling activateSession()`);
      TelemetryProvider.Instance.trackEvent(`${telemetryPrefix}_SessionExtended`);
      activateSession();
    }
  }, [activateSession, sessionState]);

  /**
   * Handle auth state changes as a result of platform or web auth.
   * WebView:
   * - Unlock the session if the platform auth was successful.
   * - Fallback to web auth if platform auth was not supported.
   * Web:
   * - Unlock the session if the MFA auth was successful.
   */
  useEffect(() => {
    const authState = isWebView
      ? `supportsLocalAuth: ${supportsLocalAuth}, authResult: ${authResult}`
      : `isMfaAuthenticated: ${isMfaAuthenticated}`;
    authlog(`ActiveSessionProvider: checking auth state change => ${authState}`);
    if (isWebView && supportsLocalAuth) {
      // Platform auth was successful
      if (authResult === AuthResult.Success) {
        authlog('ActiveSessionProvider => platform auth was successful. Calling unlockSession');
        unlockSession();
      }
      // If platform auth was not supported, fallback to web auth.
      else if (authResult === AuthResult.NotSupported) {
        authlog('ActiveSessionProvider => fallback is not supported. Unexpected response.');
        TelemetryProvider.Instance.trackException({
          name: 'UnexpectedAuthResult',
          message: 'NotSupported response from platform auth',
        });
      }
    }
    // Web MFA auth was successful
    else if (isMfaAuthenticated) {
      authlog('ActiveSessionProvider => web auth was successful. Calling unlockSession');
      unlockSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authResult, isMfaAuthenticated]);

  /**
   * Any user activity should extend the session if the session is already active.
   * Extending a session while stale has to be handled explicitly by user clicking on the WarningCard CTA.
   */
  useEffect(() => {
    authlog(`ActiveSessionProvider => sessionState: ${sessionState}`);
    if (sessionState === SessionState.Active) {
      authlog('ActiveSessionProvider => calling activateSession() & getAuthCookie()');

      activateSession({
        // Prevent update of lastActiveTime to avoid loop
        skipActiveTimeUpdate: true,
      });

      getAuthCookie();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionState, lastActiveTime]);

  const contextValue = useMemo(
    () => ({
      sessionState,
      timeUntilLocked,
      unlockSession,
      extendSession,
      isMfaAuthenticated,
      getMfaTokenSilent,
      loginMfa,
      getAuthCookie,
      hasAuthCookie,
      isCreditDialogOpen,
      setIsCreditDialogOpen,
    }),
    [
      sessionState,
      timeUntilLocked,
      unlockSession,
      extendSession,
      isMfaAuthenticated,
      getMfaTokenSilent,
      loginMfa,
      getAuthCookie,
      hasAuthCookie,
      isCreditDialogOpen,
    ]
  );

  return <ActiveSessionContext.Provider value={contextValue}>{children}</ActiveSessionContext.Provider>;
};

const NoopProvider = ({ children }: IActiveSessionProviderProps) => {
  return <ActiveSessionContext.Provider value={defaultActiveSessionState}>{children}</ActiveSessionContext.Provider>;
};

export const ActiveSessionProvider = ({ children }: IActiveSessionProviderProps) => {
  const { webViewPlatform, supportsLocalAuth } = useWebView();
  const cmAuthEnabled = pageDataService.isCMAuthCheckEnabled(webViewPlatform, supportsLocalAuth);

  return cmAuthEnabled ? (
    <ActiveSessionProviderInternal>{children}</ActiveSessionProviderInternal>
  ) : (
    <NoopProvider>{children}</NoopProvider>
  );
};
